/** Instance-owned collision-index extracted from the separately maintained Steam Punk game.
 * The host supplies clock, randomness, DOM and lifecycle; no globals or score API are shared. */
export function installSteamCollisionIndex(runtime) {
  runtime.spatialKey = function (x, y) {
    return Math.floor(x / runtime.SPATIAL_CELL) * 1000 + Math.floor(y / runtime.SPATIAL_CELL);
  };
  runtime.addToBuckets = function (buckets, body, radius = 0) {
    const minX = Math.floor((body.x - radius) / runtime.SPATIAL_CELL);
    const maxX = Math.floor((body.x + body.w + radius) / runtime.SPATIAL_CELL);
    const minY = Math.floor((body.y - radius) / runtime.SPATIAL_CELL);
    const maxY = Math.floor((body.y + body.h + radius) / runtime.SPATIAL_CELL);
    for (let gx = minX; gx <= maxX; gx += 1) {
      for (let gy = minY; gy <= maxY; gy += 1) {
        const key = gx * 1000 + gy;
        let bucket = buckets.get(key);
        if (!bucket) {
          bucket = [];
          buckets.set(key, bucket);
        }
        bucket.push(body);
      }
    }
  };
  runtime.queryPointBuckets = function (buckets, x, y) {
    const gx = Math.floor(x / runtime.SPATIAL_CELL);
    const gy = Math.floor(y / runtime.SPATIAL_CELL);
    runtime.scratchNearby.length = 0;
    for (let ox = -1; ox <= 1; ox += 1) {
      for (let oy = -1; oy <= 1; oy += 1) {
        const bucket = buckets.get((gx + ox) * 1000 + gy + oy);
        if (!bucket) continue;
        for (let i = 0; i < bucket.length; i += 1) runtime.scratchNearby.push(bucket[i]);
      }
    }
    return runtime.scratchNearby;
  };
  runtime.rebuildBrickBuckets = function () {
    runtime.game.brickBuckets.clear();
    let write = 0;
    for (let read = 0; read < runtime.game.bricks.length; read += 1) {
      const brick = runtime.game.bricks[read];
      if (brick.dead || brick.y >= runtime.H) continue;
      runtime.game.bricks[write] = brick;
      write += 1;
      runtime.addToBuckets(runtime.game.brickBuckets, brick, 8);
    }
    runtime.game.bricks.length = write;
    runtime.game.spatialDirty = false;
  };
  runtime.preventBounceStall = function (ball, axis) {
    const speed = Math.hypot(ball.vx, ball.vy);
    if (!speed) return;
    const nearHorizontal = Math.abs(ball.vy) / speed < 0.035;
    const nearVertical = Math.abs(ball.vx) / speed < 0.012;
    const sideLocked = axis === 'side' && ball.sideBounceStreak >= 10;
    const wallLocked = ball.wallBounceStreak >= 14 && (nearHorizontal || nearVertical);
    if (!sideLocked && !wallLocked) return;
    const direction = ball.y > runtime.H / 2 ? -1 : 1;
    const nudge = (Math.PI / 180) * (0.5 + Math.min(1.5, ball.age * 0.025)) * direction;
    const angle = Math.atan2(ball.vy, ball.vx) + nudge;
    ball.vx = Math.cos(angle) * speed;
    ball.vy = Math.sin(angle) * speed;
    ball.sideBounceStreak = 0;
    ball.wallBounceStreak = 0;
    ball.glow = 1;
    runtime.spark(ball.x, ball.y, '#ffd166', 12);
  };
  runtime.isPlayerFiredBall = function (ball) {
    return ball?.source === 'user' || ball?.source === 'chaos';
  };
  runtime.getBoardClearHitPowerBonus = function () {
    return Math.max(0, Math.floor(runtime.game.boardClearHitPower || 0));
  };
  runtime.getActiveChaosHitPowerBonus = function () {
    if (runtime.game.chaosTurns <= 0 || runtime.game.chaosStacks <= 0) return 0;
    return Math.max(
      0,
      Math.ceil(
        runtime.CHAOS_HIT_POWER_BASE + runtime.game.level / runtime.CHAOS_HIT_POWER_LEVEL_DIVISOR,
      ),
    );
  };
  runtime.getChaosHitPowerBonus = function (ball) {
    if (!runtime.isPlayerFiredBall(ball)) return 0;
    return runtime.getActiveChaosHitPowerBonus();
  };
  runtime.getBallHitDamageCap = function () {
    const levelValue = Math.max(1, Math.floor(runtime.game.level));
    const band =
      runtime.BALL_HIT_DAMAGE_CAP_BANDS.find((entry) => levelValue >= entry.minLevel) ||
      runtime.BALL_HIT_DAMAGE_CAP_BANDS[runtime.BALL_HIT_DAMAGE_CAP_BANDS.length - 1];
    return Math.max(band.floor, Math.floor(levelValue * band.levelShare));
  };
  runtime.capBallHitDamage = function (damage) {
    return runtime.clamp(Math.floor(damage || 1), 1, runtime.getBallHitDamageCap());
  };
  runtime.getCurrentPlayerBallDamage = function () {
    return runtime.capBallHitDamage(
      runtime.getPlayerBallBaseDamage() +
        runtime.getBoardClearHitPowerBonus() +
        runtime.getActiveChaosHitPowerBonus(),
    );
  };
  runtime.getBrickHitDamage = function (ball) {
    if (ball?.neutron) return runtime.chainDamage(runtime.NEUTRON_BOMB_POWER);
    if (Number.isFinite(ball?.power) && ball.power > 1)
      return runtime.chainDamage(runtime.capBallHitDamage(ball.power));
    if (!runtime.isPlayerFiredBall(ball)) return 1;
    return runtime.chainDamage(runtime.getCurrentPlayerBallDamage());
  };
  runtime.getBallComboBonus = function (ball) {
    return Math.max(0, Math.floor(ball?.comboScoreBonus || 0));
  };
  runtime.formatBrickHitDamageLabel = function (damage) {
    return `-${runtime.formatBigNumber(Math.max(1, Math.floor(damage || 1)))} HP`;
  };
  runtime.addScore = function (amount, x = runtime.W - 90, y = 80, label = null) {
    if (!amount) return;
    const total = runtime.applyChaosMultiplier(runtime.chainAward(amount));
    runtime.game.score += total;
    runtime.ui.score.textContent = runtime.formatPinballNumber(runtime.game.score, 12);
    runtime.addFloater(x, y, label || `+${runtime.formatBigNumber(total)}`, '#ffd166');
  };
  runtime.addCoins = function (amount, x = runtime.W - 90, y = 120, label = null) {
    if (!amount) return;
    const total = runtime.applyChaosMultiplier(runtime.chainAward(amount));
    runtime.game.coins += total;
    runtime.ui.coins.textContent = runtime.formatCashNumber(runtime.game.coins);
    runtime.addFloater(x, y, label || `+$${runtime.formatBigNumber(total)}`, '#74ff8d');
    runtime.runAutoUpgrade();
  };
  runtime.addExactCoins = function (amount, x = runtime.W / 2, y = runtime.H / 2, label = null) {
    const total = Math.max(0, Math.floor(amount));
    if (!total) return;
    runtime.game.coins += total;
    runtime.ui.coins.textContent = runtime.formatCashNumber(runtime.game.coins);
    runtime.addFloater(x, y, label || `+$${runtime.formatGroupedNumber(total)}`, '#74ff8d', {
      scale: 2.2,
      life: 2.4,
      vy: -34,
      stroke: 'rgba(20, 65, 16, 0.9)',
      shadowBlur: 38,
    });
    runtime.runAutoUpgrade();
  };
  runtime.applyChaosMultiplier = function (amount) {
    if (runtime.game.chaosTurns <= 0 || runtime.game.chaosStacks <= 0) return amount;
    return amount * 2 ** runtime.game.chaosStacks;
  };
  runtime.chaosMultiplierLabel = function () {
    return runtime.game.chaosTurns > 0 && runtime.game.chaosStacks > 0
      ? `x${2 ** runtime.game.chaosStacks}`
      : '';
  };
  runtime.addFloater = function (x, y, text, color, options = {}) {
    const maxFloaters =
      runtime.game.energyMode === 'pressureGuard'
        ? Math.max(18, Math.floor(runtime.MAX_FLOATERS * 0.52))
        : runtime.MAX_FLOATERS;
    if (runtime.game.floaters.length >= maxFloaters) {
      runtime.game.floaters.splice(0, runtime.game.floaters.length - maxFloaters + 1);
    }
    const life = options.life || 0.95;
    runtime.game.floaters.push({
      x,
      y,
      text,
      color,
      vx: runtime.visualRand(-20, 20),
      vy: options.vy || runtime.visualRand(-68, -38),
      life,
      maxLife: life,
      scale: options.scale || 1,
      stroke: options.stroke || 'rgba(37,12,3,0.75)',
      shadowBlur: options.shadowBlur || 16,
    });
  };
  runtime.scheduleEffect = function (delaySeconds, effect) {
    if (typeof effect !== 'function') return;
    runtime.game.effectQueue.push({
      at: runtime.now() + Math.max(0, delaySeconds) * 1000,
      effect,
    });
  };
}
